<template>
    <div class="banner-wrapper" :class="{ 'setas-externas': props.setasExternas }">
        <button
            v-if="props.mostrarControles && imagensCarrossel.length > 1 && props.setasExternas"
            class="banner-controle-externa"
            @click="slideAnterior"
            aria-label="Anterior"
            :style="props.estilos?.seta"
        >
            &#10094;
        </button>

        <div class="banner-editor-container" :style="containerStyle">
            <slot name="overlaySelecao" />
            <div class="banner-frame-wrapper">
                <img
                    v-for="(imagem, index) in imagensCarrossel"
                    :key="index"
                    :src="imagem.src"
                    :alt="imagem.alt"
                    class="banner-img-ajustada"
                    :style="{
                        display: index === slideAtivo ? 'block' : 'none',
                        borderRadius: props.estilos?.imagem?.borderRadius,
                        objectFit: 'contain',
                    }"
                />

                <div v-if="props.titulo && props.titulo.trim()" class="banner-titulo">
                    <h2>{{ props.titulo }}</h2>
                </div>

                <template v-if="props.mostrarControles && imagensCarrossel.length > 1 && !props.setasExternas">
                    <button class="banner-controle banner-controle-esquerda" @click="slideAnterior" aria-label="Anterior" :style="props.estilos?.seta">
                        &#10094;
                    </button>
                    <button class="banner-controle banner-controle-direita" @click="proximoSlide" aria-label="Próximo" :style="props.estilos?.seta">
                        &#10095;
                    </button>
                </template>

                <div v-if="props.mostrarIndicadores && imagensCarrossel.length > 1" class="banner-indicadores">
                    <span
                        v-for="(imagem, index) in imagensCarrossel"
                        :key="'indicador-' + index"
                        :class="['banner-indicador', { ativo: index === slideAtivo }]"
                        @click="irParaSlide(index)"
                        :style="{
                            background: index === slideAtivo ? props.estilos?.indicador?.ativo?.background : props.estilos?.indicador?.inativo?.background,
                            width: props.estilos?.indicador?.width,
                            height: props.estilos?.indicador?.height,
                            borderRadius: props.estilos?.indicador?.borderRadius,
                        }"
                    ></span>
                </div>
            </div>
        </div>

        <button
            v-if="props.mostrarControles && imagensCarrossel.length > 1 && props.setasExternas"
            class="banner-controle-externa"
            @click="proximoSlide"
            aria-label="Próximo"
            :style="props.estilos?.seta"
        >
            &#10095;
        </button>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, defineProps, defineEmits, watch } from 'vue';

const props = defineProps({
    titulo: String,
    imagens: Array,
    altura: String,
    autoPlay: Boolean,
    intervalo: Number,
    mostrarControles: Boolean,
    mostrarIndicadores: Boolean,
    estilos: Object,
    width: {
        type: [String, Number],
        default: '100%',
    },
    aspectRatio: {
        type: String,
        default: '16/9',
    },
    fundoTransparente: {
        type: Boolean,
        default: false,
    },
    setasExternas: {
        type: Boolean,
        default: false,
    },
});

const containerStyle = computed(() => ({
    width: typeof props.width === 'number' ? `${props.width}%` : props.width,
    height: props.altura || '400px',
    borderRadius: props.estilos?.container?.borderRadius,
    background: props.fundoTransparente ? 'transparent' : props.estilos?.container?.background || '#ffffff',
    boxShadow: props.estilos?.container?.boxShadow,
}));

const emit = defineEmits(['update:props']);

const slideAtivo = ref(0);
let intervalId = null;

const imagensCarrossel = computed(() => props.imagens || []);

function iniciarAutoPlay() {
    pararAutoPlay();
    if (props.autoPlay && imagensCarrossel.value.length > 1) {
        intervalId = setInterval(() => {
            proximoSlide();
        }, props.intervalo || 2000);
    }
}
function pararAutoPlay() {
    if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
    }
}

function proximoSlide() {
    if (!imagensCarrossel.value.length) return;
    slideAtivo.value = (slideAtivo.value + 1) % imagensCarrossel.value.length;
}

function slideAnterior() {
    if (!imagensCarrossel.value.length) return;
    slideAtivo.value = (slideAtivo.value - 1 + imagensCarrossel.value.length) % imagensCarrossel.value.length;
}

function irParaSlide(index) {
    slideAtivo.value = index;
}

watch(
    () => [props.autoPlay, props.intervalo, imagensCarrossel.value.length],
    ([autoPlay]) => {
        if (autoPlay) {
            iniciarAutoPlay();
        } else {
            pararAutoPlay();
        }
    },
    { immediate: true }
);

onMounted(() => {
    if (props.autoPlay) iniciarAutoPlay();
});

onUnmounted(() => {
    pararAutoPlay();
});

const propsAtualizados = computed(() => ({
    imagens: props.imagens,
    altura: props.altura,
    autoPlay: props.autoPlay,
    intervalo: props.intervalo,
    mostrarControles: props.mostrarControles,
    mostrarIndicadores: props.mostrarIndicadores,
    estilos: props.estilos,
    titulo: props.titulo,
    fundoTransparente: props.fundoTransparente,
    setasExternas: props.setasExternas,
}));

emit('update:props', { ...propsAtualizados.value });
</script>

<style scoped>
.banner-titulo {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 16px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
    color: white;
    z-index: 2;
    box-sizing: border-box;
}

.banner-editor-container {
    width: 100%;
    height: 100%;
    min-height: 80px;
    max-height: 100%;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 8px;
}

.banner-frame-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    position: relative;
}

.banner-img-ajustada {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
    display: block;
    position: relative;
    z-index: 1;
    pointer-events: none;
}

.banner-controle {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.3);
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    font-size: 22px;
    cursor: pointer;
    z-index: 2;
    transition: background 0.2s;
}

.banner-controle-esquerda {
    left: 12px;
}

.banner-controle-direita {
    right: 12px;
}

.banner-indicadores {
    position: absolute;
    bottom: 12px;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    gap: 8px;
    z-index: 2;
}

.banner-indicador {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #bbb;
    display: inline-block;
    cursor: pointer;
    transition: background 0.2s;
}

.banner-indicador.ativo {
    background: #0f97c7;
}

.image-remove-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(255, 255, 255, 0.8);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
    z-index: 3;
}

.image-remove-btn:hover {
    background: rgba(255, 255, 255, 1);
}

.banner-wrapper {
    display: flex;
    align-items: center;
    position: relative;
}

.banner-wrapper.setas-externas {
    gap: 4px;
}

.banner-controle-externa {
    position: relative;
    background: rgba(0, 0, 0, 0.3);
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    font-size: 24px;
    cursor: pointer;
    transition: background 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;
}
</style>
